// components/sections/MedidasEIncentivos.tsx

'use client';

import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/atoms/carousel";

interface MedidaItem {
  id: string;
  tag: string;
  title: string;
  description: string;
  detailsLink?: string;
}

interface MedidasEIncentivosProps {
  title?: string;
  subtitle?: string;
  descriptionMain?: string;
  descriptionHighlight?: string;
  descriptionEnd?: string;
  buttonLabel?: string;
  buttonLink?: string;
  imageUrl?: string;
  medidas?: MedidaItem[];
  onMedidaClick?: (medidaId: string) => void;
}

const defaultMedidas: MedidaItem[] = [
  {
    id: "qualificacao-1",
    tag: "+ QUALIFICAÇÃO",
    title: "Medida I",
    description: "Apoio a Programas, Projetos, Ações de Formação e Iniciativas visando o reforço da empregabilidade",
    detailsLink: "/medidas/qualificacao-1",
  },
  {
    id: "bolsa-digital",
    tag: "BOLSA CABO VERDE DIGITAL",
    title: "Medida I",
    description: "Apoio a Programas, Projetos, Ações de Formação e Iniciativas visando o reforço da empregabilidade",
    detailsLink: "/medidas/bolsa-digital",
  },
  {
    id: "competencias-1",
    tag: "+ COMPETÊNCIAS",
    title: "Medida I",
    description: "Apoio a Programas, Projetos, Ações de Formação e Iniciativas visando o reforço da empregabilidade",
    detailsLink: "/medidas/competencias-1",
  },
  {
    id: "competencias-2",
    tag: "+ COMPETÊNCIAS",
    title: "Medida I",
    description: "Apoio a Programas, Projetos, Ações de Formação e Iniciativas visando o reforço da empregabilidade",
    detailsLink: "/medidas/competencias-2",
  },
  {
    id: "competencias-1",
    tag: "+ COMPETÊNCIAS",
    title: "Medida I",
    description: "Apoio a Programas, Projetos, Ações de Formação e Iniciativas visando o reforço da empregabilidade",
    detailsLink: "/medidas/competencias-1",
  },
  {
    id: "competencias-2",
    tag: "+ COMPETÊNCIAS",
    title: "Medida I",
    description: "Apoio a Programas, Projetos, Ações de Formação e Iniciativas visando o reforço da empregabilidade",
    detailsLink: "/medidas/competencias-2",
  },
];

export default function MedidasEIncentivos({
  title = "Medidas e incentivos",
  descriptionMain = "Dispomos de várias medidas de incentivo ao emprego e à formação profissional.",
  descriptionHighlight = "Descobre qual é a melhor medida para ti",
  buttonLabel = "+ Medidas e incentivos",
  buttonLink = "/medidas-incentivos",
  imageUrl = "/news.png",
  medidas = defaultMedidas,
  onMedidaClick,
}: MedidasEIncentivosProps) {
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap());

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  const handleMedidaClick = (medidaId: string) => {
    if (onMedidaClick) {
      onMedidaClick(medidaId);
    }
  };

  // Group medidas in pairs for the grid layout
  const groupedMedidas: MedidaItem[][] = [];
  for (let i = 0; i < medidas.length; i += 4) {
    groupedMedidas.push(medidas.slice(i, i + 4));
  }

  return (
    <section className="w-full py-12 sm:py-14 md:py-16 lg:py-20 px-0 lg:px-8 bg-stone-300/20 relative">
      {/* SVG Gradient Overlay */}
      <svg
        className="absolute bottom-0 left-0 right-0 w-full h-20 sm:h-28 md:h-56 lg:h-80 xl:h-96 pointer-events-none"
        viewBox="0 0 1920 490"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="paint0_linear_3757_31277" x1="305.421" y1="138.814" x2="1743.34" y2="900.336" gradientUnits="userSpaceOnUse">
            <stop offset="0.0257575" stopColor="#EF4130" stopOpacity="0.8" />
            <stop offset="0.254366" stopColor="#F15A29" stopOpacity="0.7" />
            <stop offset="0.367337" stopColor="#F6881F" stopOpacity="0.5" />
            <stop offset="0.586538" stopColor="#F68D1E" stopOpacity="0.4" />
            <stop offset="0.764423" stopColor="#F7941D" stopOpacity="0.5" />
            <stop offset="1" stopColor="#F15A29" stopOpacity="0.7" />
          </linearGradient>
        </defs>
        {/* Triângulo branco no canto inferior esquerdo */}
        <path
          d="M0 253V490H199.477V489.782L0 253Z"
          fill="#EF4130" fillOpacity=""
        />
        {/* Path com gradiente laranja como overlay */}
        <path
          d="M1920 0V489.955H199.661V490.002L199.622 489.955H199.477V489.782L0 253V0.0488281L199.477 0.046875V0H1920Z"
          fill="transparent"
        />
      </svg>
      {/* Mobile: Image full-width outside container, Desktop: Inside container */}
      <div className="lg:container lg:mx-auto px-0 sm:px-0 lg:px-12 xl:px-16 mt-12 sm:mt-16 md:mt-20">
        {/* Main Layout - Mobile: Image with overlapping cards, Desktop: Grid */}
        <div className="lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">
          {/* Image with Overlay and Cards - Mobile: Full-width, Desktop: Right */}
          <div className="relative w-full lg:order-2">
            {/* Image Container */}
            <div className="relative w-full h-[500px] sm:h-[500px] lg:h-[490px] lg:max-h-[544px] lg:rounded-sm overflow-hidden">
              <Image
                src={imageUrl}
                alt={title}
                fill
                className="object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                priority
              />

              {/* Dark Overlay */}
              <div className="absolute inset-0 bg-black/50" />

              {/* Text Content Overlay */}
              <div className="absolute inset-0 flex flex-col justify-start items-start p-6 sm:p-8 md:p-10 lg:justify-center">
                <div className="flex flex-col gap-4 sm:gap-5 md:gap-6">
                  {/* Title */}
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold uppercase font-barlow-semi-condensed text-white leading-tight max-w-md">
                    {title}
                  </h2>

                  {/* Description */}
                  <div className="flex flex-col gap-2 sm:gap-3 max-w-xs">
                    <div className="text-sm sm:text-base md:text-lg font-nunito text-white font-normal" dangerouslySetInnerHTML={{ __html: descriptionMain }}></div>
                    <div className="text-sm sm:text-base md:text-lg font-nunito text-white font-bold" dangerouslySetInnerHTML={{ __html: descriptionHighlight }}></div>
                  </div>

                  {/* Button */}
                  <Link href={buttonLink}>
                    <button
                      className={`
                        px-5 sm:px-6 py-2.5 sm:py-3 rounded-sm
                        border-2 border-white
                        bg-transparent hover:bg-[#F15A29]/10
                        hover:border-[#F15A29]
                        transition-all duration-300
                        inline-flex items-center gap-3
                        cursor-pointer
                        w-full sm:w-auto min-w-[200px] justify-center
                      `}
                    >
                      <span className="text-sm sm:text-base font-poppins font-medium text-white ">
                        {buttonLabel}
                      </span>
                    </button>
                  </Link>
                </div>
              </div>

              {/* Cards Overlay - Only on Mobile/Tablet */}
              <div className="absolute bottom-0 left-0 right-0 lg:hidden px-4 pb-6 z-10">
                <Carousel
                  setApi={setApi}
                  opts={{
                    align: "start",
                    loop: true,
                  }}
                  className="w-full"
                >
                  <CarouselContent className="-ml-2 sm:-ml-4">
                    {medidas.map((medida) => (
                      <CarouselItem key={medida.id} className="pl-2 sm:pl-4 basis-[85%] sm:basis-[45%]">
                        <MedidaCard
                          medida={medida}
                          onClick={() => handleMedidaClick(medida.id)}
                        />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>
              </div>
            </div>
          </div>

          {/* Cards Grid - Only on Desktop */}
          <div className="hidden lg:flex flex-col gap-6 w-full lg:order-1">
            <Carousel
              setApi={setApi}
              opts={{
                align: "start",
                loop: true,
              }}
              className="w-full"
            >
              <CarouselContent>
                {groupedMedidas.map((group, groupIndex) => (
                  <CarouselItem key={groupIndex}>
                    <div className="grid grid-cols-2 gap-6">
                      {group.map((medida) => (
                        <MedidaCard
                          key={medida.id}
                          medida={medida}
                          onClick={() => handleMedidaClick(medida.id)}
                        />
                      ))}
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </div>
        </div>
        {/* Carousel Indicators */}
        {count > 1 && (
          <div className="relative z-50 flex justify-center lg:justify-start items-center gap-2 sm:gap-3 mt-8 md:mt-16 lg:ml-60 mb-12 sm:mb-16 md:mb-20 pointer-events-auto">
            {Array.from({ length: count }).map((_, index) => (
              <button
                key={index}
                onClick={() => api?.scrollTo(index)}
                className={`transition-all duration-300 h-1 sm:h-2 cursor-pointer rounded-full touch-manipulation ${index === current
                  ? "w-16 sm:w-20 bg-gradient-to-r from-orange-600 via-orange-500 to-red-500"
                  : "w-8 sm:w-10 bg-gray-300"
                  }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

interface MedidaCardProps {
  medida: MedidaItem;
  onClick?: () => void;
}

function MedidaCard({ medida, onClick }: MedidaCardProps) {
  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  return (
    <Link href={medida.detailsLink || "#"} className="block h-full">
      <div
        onClick={handleClick}
        className="bg-white rounded-sm shadow-md p-5 sm:p-6 md:p-7 lg:p-8 flex flex-col justify-between gap-5 sm:gap-6 md:gap-7 lg:gap-8 hover:shadow-lg transition-shadow duration-300 cursor-pointer group h-full"
      >
        {/* Content Section */}
        <div className="flex flex-col gap-2 sm:gap-3">
          {/* Tag and Title */}
          <div className="flex flex-col gap-1.5 sm:gap-2">
            <span className="text-orange-400 text-base sm:text-lg md:text-xs xl:text-base font-medium font-nunito-sans uppercase line-clamp-1">
              {medida.title}
            </span>
            <h3 className="text-2xl sm:text-2xl md:text-3xl font-semibold font-nunito text-zinc-800 line-clamp-1">
              {medida.tag}
            </h3>
          </div>

          {/* Description */}
          <div className="text-sm sm:text-base font-nunito text-gray-400 leading-relaxed truncate html-content line-clamp-2" dangerouslySetInnerHTML={{ __html: medida.description }}></div>
        </div>

        {/* Details Link */}
        <div className="text-orange-400 text-base sm:text-lg font-normal font-poppins hover:underline cursor-pointer group-hover:translate-x-1 transition-transform duration-300">
          + detalhes
        </div>
      </div>
    </Link>
  );
}